import { createEffect, createEvent, createStore, sample } from 'effector'
import { createGate } from 'effector-react'
import { and, debounce, not } from 'patronum'

import type { PortalProfile } from '@portal/portal-api'

import { initWrapper } from '@/app/wrapper'
import {
    $gameConnected,
    clientRuntimeSelected,
    gameConnectFx,
    preloaderReady,
    resetPlayPageState,
} from '@/entities/game'
import * as profile from '@/entities/profile/portal'
import { accountProtectionCheckCalled } from '@/features/account-protection'
import { AccountReloginDialog, trackRelogin } from '@/features/account-relogin'
import { isFeatureEnabled } from '@/shared/config'
import { navigateTo } from '@/shared/lib/next'
import { persist } from '@/shared/lib/persist/local'
import { isNotNull } from '@/shared/lib/types'

export const PlayPageGate = createGate()

export const $isPreloaderReady = createStore(false)
    .on(preloaderReady, () => true)
    .on(PlayPageGate.close, () => false)

const playPageOpened = debounce({ source: PlayPageGate.open, timeout: 0 })

const initWrapperFx = createEffect(initWrapper)

sample({
    clock: playPageOpened,
    target: initWrapperFx,
})

const playPageProfileReceived = sample({
    clock: [profile.$portalProfile, playPageOpened, clientRuntimeSelected],
    source: profile.$portalProfile,
    filter: isNotNull<PortalProfile>,
})

// play login

sample({
    clock: playPageProfileReceived,
    filter: and(PlayPageGate.status, not($gameConnected), not(gameConnectFx.pending)),
    fn: () => ({ phase: 'initial' as const }),
    target: gameConnectFx,
})

// cleanup game connect result and client references on page close
sample({
    clock: PlayPageGate.close,
    target: resetPlayPageState,
})

// redirect to home page if user is not authorized
sample({
    clock: profile.unauthorized,
    filter: PlayPageGate.status,
    target: navigateTo('/', { replace: true }),
})

// account protection check

sample({
    // NOTE: проверяем аккаунт только игрокам с FB
    clock: playPageProfileReceived.filter({ fn: (profile) => profile.platformType === 'fb' }),
    filter: PlayPageGate.status,
    target: accountProtectionCheckCalled,
})

// relogin tracker

const reloginTracker = trackRelogin()

sample({
    clock: reloginTracker.reloginDetected,
    filter: PlayPageGate.status,
    target: AccountReloginDialog.model.showFx,
})

// chat width

export const $chatWidth = createStore(360)

persist({
    store: $chatWidth,
    key: 'chat-width',
})

export const chatWidthChanged = createEvent<number>()

sample({
    clock: chatWidthChanged,
    target: $chatWidth,
})

// PUSHWOOSH

let browserNotificationModule: Promise<typeof import('@/features/browser-notification')>

function getBrowserNotificationModule() {
    if (!browserNotificationModule) {
        browserNotificationModule = import('@/features/browser-notification')
    }
    return browserNotificationModule
}

if (isFeatureEnabled('pushwoosh')) {
    sample({
        clock: playPageOpened,
        // NOTE: Pre-load browser notification module when play page opens
        target: createEffect(getBrowserNotificationModule),
    })
}

const updatePlayerIdFx = createEffect(async (publicId: string) => {
    const { updatePublicIdRequested } = await getBrowserNotificationModule()

    updatePublicIdRequested(publicId)
})

if (isFeatureEnabled('pushwoosh')) {
    sample({
        clock: playPageProfileReceived,
        fn: ({ publicId }) => publicId,
        target: updatePlayerIdFx,
    })
}
